import { HelpLink, Related, ScreenLink } from "./parts";

export function Reconciliation() {
  return (
    <>
      <p>
        <ScreenLink href="/reconciliation">Reconciliation</ScreenLink> checks that the usage Nexus bills is complete and
        matches what AWS recorded. Pick a customer, and optionally a date range, installation or model. It has two reports.
        Read both before closing a month: see <HelpLink to="closing-a-month" section="before-you-close" />.
      </p>

      <h2 id="completeness">Completeness</h2>
      <p>
        Per installation: how many requests the proxy served (from its own counters), how many usage events Nexus received,
        and how many were delayed (spilled to storage for a retry) or lost.
      </p>
      <ul>
        <li>
          <strong>Completeness</strong> = events received ÷ requests served. 10,000 requests and 9,990 events is 99.9 %.
        </li>
        <li>
          “No counters” for a stretch means the proxy sent no counter data then. That is a gap to explain, not 0 %.
        </li>
        <li>
          Lost and unmetered requests do not stop a close. They appear as discrepancy lines on the month's invoice, so
          chasing them first means fewer lines to explain.
        </li>
      </ul>

      <h2 id="drift">Bedrock drift</h2>
      <p>
        Per installation, UTC day and model (up to 92 days at a time): Nexus's own token and request counts next to what
        Bedrock reported to CloudWatch for the same account.
      </p>
      <p>
        <strong>Drift</strong> = (Nexus − Bedrock) ÷ Bedrock. Nexus 990,000 tokens against Bedrock 1,000,000 is −1 %. An
        alarm fires above 1 % on a cell with at least 10,000 Bedrock tokens.
      </p>
      <p>Read the findings on each row before the number. Some differences are expected and need no action:</p>
      <ul>
        <li>
          <strong>Other use of the account.</strong> CloudWatch counts all Bedrock use in the AWS account, not only the
          proxy's, so drift is negative when people also call Bedrock directly. Installations that share an account are
          flagged.
        </li>
        <li>
          <strong>Requests lean negative.</strong> Bedrock counts requests the client abandoned after Bedrock finished;
          Nexus does not.
        </li>
        <li>
          <strong>1-hour cache writes</strong> are never compared: Bedrock reports all cache writes as one number.
        </li>
      </ul>
      <p>
        Positive drift that nothing explains (Nexus counts more than Bedrock) is a metering fault: raise it before the
        invoice goes out.
      </p>
      <p>
        AWS cost reconciliation (the Cost and Usage Report against metered tokens) is on{" "}
        <ScreenLink href="/aws-costs">AWS costs</ScreenLink>; see <HelpLink to="aws-costs" section="finalizing" />.
      </p>

      <Related to={["closing-a-month", "installations", "aws-costs"]} />
    </>
  );
}
